import CloseIcon from "@mui/icons-material/Close";
import { Button, CircularProgress, Dialog, TextField, Typography } from "@mui/material";
import Cookies from "js-cookie";
import { useRef, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { setToken, isAuthenticated } from "./app/components/api/auth";
import axios from "./app/components/api/axios";
import PasswordCreate from "./app/components/cm/Dashboard/FullPages/PasswordCreate";
import ModalButton from "./app/components/global/ModalButton";
import ResponseModal from "./app/components/global/ResponseModal";
import { NetworkBackground } from "./app/components/NetworkBackground";
import { userDBService } from "./app/components/ui library/helpers/dbInstances";
import { invalidateSession } from "./app/components/ui library/helpers/functions";
import { inputStyles } from "./app/components/ui library/styles/loginStyles";
import DataContext from "./app/context/DataContext";
import { useQueryParams } from "./app/hooks/useQueryParams";
import { privacyLink, termsLink } from "./globalConstants";
import { inject } from "./lib/itn-ui-kit/services/service-container";

function LoginFooter() {
  return (
    <div className="absolute bottom-[50px] flex w-full justify-center gap-4">
      <a
        className="font-bold normal-case text-white hover:bg-transparent hover:underline"
        href={privacyLink}
        rel="noreferrer"
        target="_blank"
      >
        Privacy Policy
      </a>
      <a
        className="font-bold normal-case text-white hover:bg-transparent hover:underline"
        href={termsLink}
        rel="noreferrer"
        target="_blank"
      >
        Terms & Conditions
      </a>
    </div>
  );
}

const Login = () => {
  const currentUserService = inject("current-user-service");
  const {
    accessToken,
    errorMessage,
    loggedInUser,
    setAccessToken,
    setCaseManagerPk,
    setErrorMessage,
    setLoggedInUser,
    setUserRoles,
    setUserType,
  } = useContext(DataContext);
  const inputRef = useRef();
  const navigate = useNavigate();
  const { filters } = useQueryParams();
  const adminAuthToken = filters.get("auth_token"); // Allows login via admin panel link
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [needsCreatePassword, setNeedsCreatePassword] = useState(false);
  const [countLoginAttempts, setCountLoginAttempts] = useState(0);
  const [showResetPassModal, setShowResetPassModal] = useState(false);
  const [successResetEmail, setSuccessResetEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function handleChange(key, value) {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }

  async function handleSubmit(adminPanelAuthToken = null) {
    setIsLoading(true);
    let response = null;
    if (adminPanelAuthToken) {
      if (isAuthenticated()) {
        await invalidateSession({ skipRedirect: true });
      }
      setToken(adminPanelAuthToken);
    }

    try {
      if (adminPanelAuthToken) {
        response = await axios.get("/api/users/me", {});
      } else {
        response = await axios.post("/auth/login", formData, {
          headers: { "Content-Type": "application/json" },
        });
      }
      const { data, config } = response;
      const userData = data?.user ? data.user : data;
      const currentToken = adminPanelAuthToken ?? data.token;
      await userDBService.save("baseURL", config.baseURL);
      setLoggedInUser(userData);
      setAccessToken(currentToken);
      if (userData.requires_password_change) {
        setNeedsCreatePassword(true);
        return;
      }
      setCookies(userData, currentToken);
    } catch (err) {
      if (err.response) {
        setCountLoginAttempts((prev) => prev + 1);
        const status = err.response.status;
        if (!status) {
          setErrorMessage("No Server Response");
        } else if (status >= 400 && status < 500) {
          setErrorMessage("Check the account information you entered and try again.");
        } else {
          setErrorMessage("Login Failed");
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handlePasswordReset() {
    try {
      await axios.post(
        "/api/password_reset/",
        { email },
        {
          "Content-Type": "application/json",
        }
      );
      setEmail("");
      setCountLoginAttempts(0);
      setSuccessResetEmail(true);
      setShowResetPassModal(false);
    } catch (error) {
      const res = error.response.data;
      const errArr = [];
      for (const item in res) {
        errArr.push(item + `: ${res[item]}`);
      }
      setErrorMessage(errArr);
      setIsError(true);
    }
  }

  async function setCookies(userResp, token) {
    setToken(token);
    Cookies.set("pnCmPk", userResp.pk);
    Cookies.set("userType", !userResp.entity ? "unknown" : userResp.entity.entity_type_label);
    currentUserService.user = userResp;
    currentUserService.userRoles = {
      groups: userResp.groups,
      permissions: userResp.permissions,
    };
    setUserRoles({
      groups: userResp.groups,
      permissions: userResp.permissions,
    });
    await userDBService.save("roles", userResp.groups);
    await userDBService.save("permissions", userResp.permissions);
    await userDBService.save("routes", userResp.routes);
    await userDBService.save("routesMap", userResp.route_map);
    await userDBService.save("userDetail", userResp);
    setCaseManagerPk(userResp.pk);
    setUserType(!userResp.entity ? "unknown" : userResp.entity.entity_type_label);
    navigate("/landing");
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  useEffect(() => {
    if (adminAuthToken) {
      handleSubmit(adminAuthToken);
    }
  }, [adminAuthToken]);

  // Mode: user must create a new password before continuing
  if (needsCreatePassword) {
    return (
      <>
        <NetworkBackground />
        <PasswordCreate userResponse={loggedInUser} setCookies={setCookies} token={accessToken} />
        <LoginFooter />
      </>
    );
  }

  // Mode: standard login form
  return (
    <>
      <NetworkBackground />

      <div className="flex h-screen items-center justify-center">
        <div className="flex w-fit rounded-lg border-l-[10px] border-[#257df8] bg-white">
          <div className="flex flex-col pl-9 pr-12">
            <img
              alt="HIS Logo"
              src="/assets/his-logo.svg"
              className="mt-10 block w-[150px] cursor-pointer duration-500"
            />
            <Typography className="mt-[50px] text-[32px] font-light leading-[1]">
              Welcome Back
            </Typography>
            <Typography className="text-[48px] font-light leading-[1]">Please Sign In</Typography>
            <Typography className="mt-[14px] text-[14px] font-light leading-[1] text-gray-500">
              Please enter your username and password
            </Typography>
            <form
              id="login-form"
              className="mt-[14px] grid grid-rows-3 gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                await handleSubmit();
              }}
            >
              <TextField
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                label="Username"
                name="username"
                onChange={(e) => handleChange("username", e.target.value)}
                inputRef={inputRef}
                type="text"
                value={formData.username}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                }}
                required
                sx={inputStyles}
              />
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Password"
                name="password"
                onChange={(e) => handleChange("password", e.target.value)}
                type="password"
                value={formData.password}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                }}
                required
                sx={inputStyles}
              />
              <div className="mt-3 flex items-center gap-[10px]">
                <Button
                  className="px-9 py-2 font-normal"
                  disabled={isLoading}
                  sx={{ backgroundColor: "#257df8" }}
                  type="submit"
                  variant="contained"
                >
                  {isLoading ? (
                    <>
                      Loading... <CircularProgress className="ml-2" size={20} color="inherit" />
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>
                {errorMessage && <p className="text-[0.9rem] text-red-500">{errorMessage}</p>}
              </div>
              {countLoginAttempts > 1 && (
                <div className="mt-2 pb-10">
                  <Button
                    onClick={() => setShowResetPassModal(true)}
                    className="text-purple-600 hover:bg-purple-600 hover:text-white"
                  >
                    Forgot password? Click here to reset
                  </Button>
                </div>
              )}
            </form>
          </div>
          <div className="flex">
            <img src="/assets/his-login-image.png" className="h-[518px]" alt="Login" />
          </div>
        </div>
      </div>

      <LoginFooter />

      <Dialog open={showResetPassModal}>
        <div className="relative flex w-[400px] flex-col items-center p-5">
          <div className="flex w-full justify-end text-xl">
            <Button onClick={() => setShowResetPassModal(false)}>
              <CloseIcon />
            </Button>
          </div>
          <h1 className="mb-4 text-center text-xl font-bold text-orange-400">
            Send Reset Password Email
          </h1>
          <TextField
            autoComplete="off"
            className="my-4 w-full text-center"
            InputLabelProps={{ shrink: true }}
            label="Email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Account Email"
            type="text"
            value={email}
          />
        </div>
        <div style={{ margin: "0 0 2rem 0" }}>
          <ModalButton action={handlePasswordReset} baseColor={"#FFA500"} content={"Send"} />
        </div>
      </Dialog>

      {successResetEmail && (
        <ResponseModal
          description="Please check your email for a link to reset your password!"
          openBool
          setOpenBool={setSuccessResetEmail}
          title="Success"
        />
      )}

      {isError && (
        <ResponseModal
          errorMessage={errorMessage}
          isError={true}
          openBool
          setOpenBool={setIsError}
          title="Password update failed"
        />
      )}
    </>
  );
};

export default Login;
